/**
 * QrTourist API client (CRA / fetch / localStorage).
 * Ported from Expo TS version, using REACT_APP_BACKEND_URL.
 */

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

const DEVICE_ID_KEY = "qrtourist:device_id";
const ADMIN_TOKEN_KEY = "qrtourist:admin_token";
const LANG_KEY = "qrtourist:lang";

export const SUPPORTED_LANGS = ["it", "en", "de", "fr", "es"];

export const LANG_INFO = {
  it: { flag: "🇮🇹", label: "Italiano" },
  en: { flag: "🇬🇧", label: "English" },
  de: { flag: "🇩🇪", label: "Deutsch" },
  fr: { flag: "🇫🇷", label: "Français" },
  es: { flag: "🇪🇸", label: "Español" },
};

export const ITALIAN_REGIONS = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
  "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto", "Estero",
];

export function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = "dev_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 10);
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export function getLang() {
  const v = localStorage.getItem(LANG_KEY);
  return SUPPORTED_LANGS.includes(v) ? v : "it";
}

export function setLang(l) {
  localStorage.setItem(LANG_KEY, l);
}

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminToken(token) {
  if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
}

async function request(path, init = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(init.headers || {}),
  };
  const res = await fetch(`${BASE_URL}/api${path}`, { ...init, headers });
  if (!res.ok) {
    let txt = "";
    try {
      const j = await res.json();
      txt = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail ?? j);
    } catch {
      txt = await res.text();
    }
    throw new Error(`${res.status}: ${txt}`);
  }
  return res.json();
}

async function adminRequest(path, init = {}) {
  const token = getAdminToken();
  if (!token) throw new Error("401: Not authenticated");
  return request(path, {
    ...init,
    headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` },
  });
}

export const api = {
  // Public
  listMuseums: () => request("/museums"),
  getMuseum: (id) => request(`/museums/${id}`),
  getArtwork: (id) => {
    const dev = getDeviceId();
    const lang = getLang();
    return request(`/artworks/${id}?device_id=${dev}&lang=${lang}`);
  },
  getArtworkByQr: (code) => {
    const dev = getDeviceId();
    const lang = getLang();
    return request(`/artworks/by-qr/${encodeURIComponent(code)}?device_id=${dev}&lang=${lang}`);
  },
  getAudio: (artworkId, type) => {
    const dev = getDeviceId();
    const lang = getLang();
    return request(`/artworks/${artworkId}/audio?type=${type}&device_id=${dev}&lang=${lang}`);
  },
  checkPurchases: () => {
    const dev = getDeviceId();
    return request(`/purchases/check?device_id=${dev}`);
  },
  createCheckout: (params) => {
    const dev = getDeviceId();
    return request("/checkout/session", {
      method: "POST",
      body: JSON.stringify({ ...params, device_id: dev }),
    });
  },
  checkoutStatus: (sessionId) => request(`/checkout/status/${sessionId}`),
  listRegions: (kind) => request(`/regions${kind ? `?kind=${kind}` : ""}`),
  getMuseumPlaces: (museumId, type) =>
    request(`/museums/${museumId}/places${type ? `?type=${type}` : ""}`),

  // Admin
  adminLogin: (email, password) =>
    request("/admin/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  adminMe: () => adminRequest("/admin/me"),
  adminStats: () => adminRequest("/admin/stats"),
  adminAnalytics: () => adminRequest("/admin/analytics"),
  adminPurchases: () => adminRequest("/admin/purchases"),

  adminListUsers: () => adminRequest("/admin/users"),
  adminCreateUser: (body) =>
    adminRequest("/admin/users", { method: "POST", body: JSON.stringify(body) }),
  adminUpdateUser: (id, body) =>
    adminRequest(`/admin/users/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  adminDeleteUser: (id) => adminRequest(`/admin/users/${id}`, { method: "DELETE" }),

  adminListMuseums: () => adminRequest("/admin/museums"),
  adminCreateMuseum: (body) =>
    adminRequest("/admin/museums", { method: "POST", body: JSON.stringify(body) }),
  adminUpdateMuseum: (id, body) =>
    adminRequest(`/admin/museums/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  adminDeleteMuseum: (id) => adminRequest(`/admin/museums/${id}`, { method: "DELETE" }),

  adminListArtworks: (museumId) =>
    adminRequest(`/admin/artworks${museumId ? `?museum_id=${museumId}` : ""}`),
  adminGetArtwork: (id) => adminRequest(`/admin/artworks/${id}`),
  adminCreateArtwork: (body) =>
    adminRequest("/admin/artworks", { method: "POST", body: JSON.stringify(body) }),
  adminUpdateArtwork: (id, body) =>
    adminRequest(`/admin/artworks/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  adminDeleteArtwork: (id) => adminRequest(`/admin/artworks/${id}`, { method: "DELETE" }),
  adminGetArtworkQr: (id) => adminRequest(`/admin/artworks/${id}/qr`),

  adminTranslate: (artwork_id, target_langs) =>
    adminRequest("/admin/translate", {
      method: "POST",
      body: JSON.stringify({ artwork_id, target_langs }),
    }),
  adminGenerateText: (body) =>
    adminRequest("/admin/generate-text", { method: "POST", body: JSON.stringify(body) }),
  adminPreviewAudio: (body) =>
    adminRequest("/admin/preview-audio", { method: "POST", body: JSON.stringify(body) }),
  adminGeocode: (body) =>
    adminRequest("/admin/geocode", { method: "POST", body: JSON.stringify(body) }),

  adminListPlaces: (museumId, type) => {
    const qs = [
      museumId ? `museum_id=${museumId}` : "",
      type ? `type=${type}` : "",
    ].filter(Boolean).join("&");
    return adminRequest(`/admin/places${qs ? `?${qs}` : ""}`);
  },
  adminCreatePlace: (body) =>
    adminRequest("/admin/places", { method: "POST", body: JSON.stringify(body) }),
  adminUpdatePlace: (id, body) =>
    adminRequest(`/admin/places/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  adminDeletePlace: (id) => adminRequest(`/admin/places/${id}`, { method: "DELETE" }),
};
